import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from 'moment';
import PanelTemplate from "../../../templates/Panel";
import Table from "../../../components/Table";
import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import InputSelect from "../../../components/InputSelect";
import { createOptions } from "../../../helpers/selects";
import Button from "../../../components/Button";
import { toast } from "react-toastify";
import Modal from "../../../components/Modal";
import Box from "../../../components/Box";
import Icon from "../../../components/Icon";


const DEFAULT_INTEGRATION_OPTIONS = [
    { label: 'Asia Imports', value: 'asia', tradeName: 'Asia Imports' },
    { label: 'SPOT Gifts', value: 'spot', tradeName: 'SPOT Gifts' },
    { label: 'XBZ', value: 'xbz', tradeName: 'XBZ' },
    { label: 'Só Marcas', value: 'somarcas', tradeName: 'Só Marcas' }
];

export const CompanyCategoryList = () => {
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [updatingProducts, setUpdatingProducts] = useState(false);
    const [companyCategories, setCompanyCategories] = useState([]);
    const [categories, setCategories] = useState([]);
    const [integrationOptions, setIntegrationOptions] = useState(DEFAULT_INTEGRATION_OPTIONS);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [pendingIntegration, setPendingIntegration] = useState(null);

    const [selectedIntegration, setSelectedIntegration] = useState(DEFAULT_INTEGRATION_OPTIONS[0].value);

    useEffect(() => {
        getCategories();
        getCompanyCategories();
    }, []);

    useEffect(() => {
        setIntegrationOptions(DEFAULT_INTEGRATION_OPTIONS);
        setSelectedIntegration(prev => {
            const stillExists = DEFAULT_INTEGRATION_OPTIONS.some(option => option.value === prev);
            return stillExists ? prev : DEFAULT_INTEGRATION_OPTIONS[0].value;
        });
    }, []);

    useEffect(() => {
        getSuppliers();
    }, []);

    const getCategories = () => {
        setUpdating(true);

        api.get(`/category`).then(res => {
            let toCategories = [];
            res.data.resources.forEach(category => {
                toCategories.push(category);
                const subcategories = Array.isArray(category.categories) ? category.categories : [];
                subcategories.forEach(subcategory => toCategories.push({
                    ...subcategory,
                    name: `${category.name} > ${subcategory.name}`
                }));
            });
            setCategories(toCategories);
        }).catch(error => {
            renderError(error);
        }).then(() => setUpdating(false));
    }

    const updateCategory = useCallback((companyCategory, categoryId) => {
        api.put(`/company-category/${companyCategory.id}`, {
            category_id: categoryId
        }).then(res => {
            setCompanyCategories(prev => prev.map(currentCompanyCategory => ({
                ...currentCompanyCategory,
                category_id: currentCompanyCategory.id === companyCategory.id ? categoryId : currentCompanyCategory.category_id
            })));
        }).catch(error => renderError(error));
    }, []);

    const getCompanyCategories = () => {
        setLoading(true);

        api.get(`/company-category`).then(res => {
            setCompanyCategories(res.data.resources);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const updateProducts = integrationName => {
        setUpdatingProducts(true);
        const encodedIntegrationName = encodeURIComponent(integrationName);
        api.put(`/company-category/update-product/${encodedIntegrationName}`).then(res => {
            toast.success('Produtos atualizados');
        }).catch(error => {
            renderError(error);
        }).then(() => {
            setUpdatingProducts(false);
            setPendingIntegration(null);
        });
    }

    const getSuppliers = () => {
        api.get(`/supplier`).then(res => {
            const resources = res?.data?.resources;
            const supplierList = Array.isArray(resources?.data) ? resources.data : Array.isArray(resources) ? resources : [];

            const supplierOptions = supplierList
                .map(supplier => {
                    const label = supplier?.trade_name ?? supplier?.name ?? `Fornecedor ${supplier?.id ?? ''}`;
                    const value = supplier?.slug ?? supplier?.integration_code ?? supplier?.code ?? supplier?.id?.toString() ?? label;
                    if (!value || !label) return null;
                    return { label, value, tradeName: label };
                })
                .filter(Boolean);

            if (supplierOptions.length === 0) return;

            setIntegrationOptions(prevOptions => {
                const uniqueOptions = new Map();

                DEFAULT_INTEGRATION_OPTIONS.forEach(option => uniqueOptions.set(option.value, option));
                prevOptions.forEach(option => uniqueOptions.set(option.value, option));
                supplierOptions.forEach(option => {
                    if (!uniqueOptions.has(option.value)) {
                        uniqueOptions.set(option.value, option);
                    }
                });

                const updatedOptions = Array.from(uniqueOptions.values());

                setSelectedIntegration(prevSelected => {
                    if (!prevSelected) return updatedOptions[0]?.value ?? null;
                    return updatedOptions.some(option => option.value === prevSelected) ? prevSelected : updatedOptions[0]?.value ?? null;
                });

                return updatedOptions;
            });
        }).catch(error => {
            renderError(error);
        });
    }

    const tableData = useMemo(() => {
        const header = ['Categoria fornecedor', 'Categoria interna', 'Cadastrada em'];
        const rowsByIntegration = integrationOptions.reduce((acc, option) => {
            acc[option.value] = [];
            return acc;
        }, {});

        companyCategories.forEach(companyCategory => {
            const type = companyCategory.company_integration;
            const externalCategory = `${companyCategory.company_category_name}${companyCategory.company_subcategory_name ? ` > ${companyCategory.company_subcategory_name}` : ''}`;
            if (!rowsByIntegration[type]) {
                rowsByIntegration[type] = [];
            }

            rowsByIntegration[type].push({
                data: [
                    <span className="company-category-table__external">
                        <Icon name="tag" size={14} />
                        <span>{externalCategory}</span>
                    </span>,
                    <InputSelect 
                        loading={updating} 
                        value={companyCategory.category_id} 
                        change={categoryId => updateCategory(companyCategory, categoryId)} 
                        options={createOptions(categories, 'name', 'id', true)}
                        removeMargin={true}
                    />,
                    <span className="company-category-table__date">{moment(companyCategory.created_at).format('DD/MM/YYYY HH:mm')}</span>
                ],
                action: () => null
            });
        });

        return { header, rows: rowsByIntegration };
    }, [categories, companyCategories, integrationOptions, updateCategory, updating]);

    const selectedRows = useMemo(() => tableData.rows[selectedIntegration] ?? [], [tableData.rows, selectedIntegration]);
    const selectedIntegrationOption = useMemo(() => (
        integrationOptions.find(option => option.value === selectedIntegration) ?? integrationOptions[0]
    ), [integrationOptions, selectedIntegration]);
    const selectedCompanyCategories = useMemo(() => (
        companyCategories.filter(companyCategory => companyCategory.company_integration === selectedIntegration)
    ), [companyCategories, selectedIntegration]);
    const summary = useMemo(() => {
        const total = selectedCompanyCategories.length;
        const mapped = selectedCompanyCategories.filter(companyCategory => !!companyCategory.category_id).length;
        const pending = total - mapped;

        return { total, mapped, pending };
    }, [selectedCompanyCategories]);

    const handleOpenConfirmModal = () => {
        const selectedOption = integrationOptions.find(option => option.value === selectedIntegration);
        if (!selectedOption) return;
        setPendingIntegration(selectedOption);
        setConfirmModalVisible(true);
    };

    const handleConfirmUpdateProducts = () => {
        if (!pendingIntegration) return;
        const integrationName = pendingIntegration.value ?? pendingIntegration.label ?? pendingIntegration.tradeName;
        updateProducts(integrationName);
        setConfirmModalVisible(false);
    };

    return (
        <PanelTemplate id="company-categories-list">
            <Modal
                show={confirmModalVisible}
                close={() => {
                    if (!updatingProducts) {
                        setConfirmModalVisible(false);
                        setPendingIntegration(null);
                    }
                }}
                title="Atualizar categorias dos produtos"
                footer={
                    <>
                        <Button
                            type="secondary"
                            size="regular"
                            text="Cancelar"
                            action={() => {
                                if (!updatingProducts) {
                                    setConfirmModalVisible(false);
                                    setPendingIntegration(null);
                                }
                            }}
                            disabled={updatingProducts}
                        />
                        <Button
                            type="primary"
                            btnClass="btn-integration"
                            size="regular"
                            text="Atualizar categorias"
                            action={handleConfirmUpdateProducts}
                            loading={updatingProducts}
                        />
                    </>
                }
            >
                <div className="section section-confirm-update">
                    <span className="section-confirm-update__icon">
                        <Icon name="alert" size={22} />
                    </span>
                    <h3>Tem certeza de que deseja atualizar as categorias de todos os produtos deste fornecedor?</h3>
                    <p className="warning">Atenção: categorias definidas manualmente serão substituídas.</p>
                </div>
            </Modal>
            <div className="company-category-page">
                <div className="company-category-header">
                    <div className="company-category-header__copy">
                        <span className="company-category-header__eyebrow">
                            <Icon name="waypoints" size={15} />
                            Integrações
                        </span>
                        <h1>Categorias dos fornecedores</h1>
                        <p>Relacione as categorias externas às categorias internas do catálogo.</p>
                    </div>
                    <div className="company-category-header__controls">
                        <div className="company-category-header__select">
                            <span>Fornecedor</span>
                            <InputSelect
                                value={selectedIntegration}
                                change={value => setSelectedIntegration(value)}
                                options={integrationOptions}
                                removeMargin={true}
                            />
                        </div>
                        <Button
                            type={`primary`}
                            size={`small`}
                            text={`Atualizar produtos`}
                            svg="refresh"
                            loading={updatingProducts}
                            action={handleOpenConfirmModal}
                        />
                    </div>
                </div>

                <div className="company-category-summary" aria-label="Resumo do mapeamento">
                    <div className="company-category-summary__item">
                        <span>Total externo</span>
                        <strong>{summary.total}</strong>
                        <em>categorias recebidas</em>
                    </div>
                    <div className="company-category-summary__item company-category-summary__item--success">
                        <span>Vinculadas</span>
                        <strong>{summary.mapped}</strong>
                        <em>com categoria interna</em>
                    </div>
                    <div className="company-category-summary__item company-category-summary__item--warning">
                        <span>Pendentes</span>
                        <strong>{summary.pending}</strong>
                        <em>sem vínculo definido</em>
                    </div>
                </div>

                <div className="company-category-notice">
                    <Icon name="alert" size={18} />
                    <span>Alterar uma categoria no select salva o vínculo para os próximos produtos importados. Produtos já cadastrados só recebem essa mudança depois de clicar em "Atualizar produtos".</span>
                </div>

                <Box
                    className="company-category-table no-padding with-pagination"
                    title={`Mapeamento: ${selectedIntegrationOption?.label ?? 'Fornecedor'}`}
                    tools={<span className="company-category-table__counter">{summary.mapped}/{summary.total} vinculadas</span>}
                >
                    <Table header={tableData.header} rows={selectedRows} loading={loading} />
                </Box>
            </div>
        </PanelTemplate>
    );
}

export default CompanyCategoryList;
